import { useI18N } from "@sundae/react-hooks";
import {
  Badge,
  Balance,
  InfoCircledIcon,
  Percentage,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import cx from "classnames";
import { FC, useCallback } from "react";
import { useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import { TPool } from "../../../types/Pool.types";
import { getAssetName } from "../../../utils/assets.utils";
import AssetAvatar from "../../AssetAvatar";
import PoolComponent from "../../PoolComponent";

interface INewPositionItemProps {
  asset: IMappedAsset<IAssetMetaData>;
  onClick: (asset: IMappedAsset<IAssetMetaData>) => void;
  pool?: TPool;
  hra?: number;
  totalAssetAmount: AssetAmount<IAssetMetaData>;
}

const containerCx = cx(
  toolkitCx.layout.background.card,
  toolkitCx.layout.background.hoverable,
  toolkitCx.layout.transition.default,
  toolkitCx.layout.border.default,
  toolkitCx.layout.border.weight.default,
  toolkitCx.recipes.scaleWhenActive
);

const NewPositionItem: FC<INewPositionItemProps> = ({
  asset,
  onClick,
  pool,
  totalAssetAmount,
  hra,
}) => {
  const { currentLocale, t } = useI18N("farms");
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();

  const handleOnClick = useCallback(() => onClick(asset), [asset, onClick]);

  return (
    <li
      className={cx(
        containerCx,
        "flex w-full cursor-pointer select-none items-center justify-between rounded-lg px-4 py-2"
      )}
      onClick={handleOnClick}
    >
      <div className="grid gap-2">
        <Text size="2xs" tag="span" variant="silent">
          {t(pool ? "lpToken" : "asset", { ns: "glossary", count: 1 })}
        </Text>

        <div className="flex items-center gap-2">
          {pool ? (
            <div className="grid gap-2">
              <PoolComponent
                pool={pool}
                badges={{ ident: false, lpFee: false, version: true }}
                inline
              />
              {hra && (
                <div className="flex items-center justify-center">
                  <Tooltip
                    trigger={
                      <Badge size="xs" variant="secondary" className="!rounded-r-none">
                        <Text
                          tag="span"
                          size="2xs"
                          className="inline-flex items-center justify-start gap-1"
                        >
                          {t("hra")}
                          <InfoCircledIcon className="h-3 w-3" />
                        </Text>
                      </Badge>
                    }
                    tooltipLabel={t("hraLegalText")}
                  />
                  <Badge size="xs" variant="gradient" className="!inline-flex !rounded-l-none">
                    <Tooltip trigger={t("hra")} tooltipLabel={t("hraLegalText")} />
                    <Percentage size="2xs" value={hra} variant="forceLight" />
                  </Badge>
                </div>
              )}
            </div>
          ) : (
            <>
              <AssetAvatar asset={asset.metadata} size="sm" />
              <Text size="sm" tag="span">
                {getAssetName(asset.metadata)}
              </Text>
            </>
          )}
        </div>
      </div>

      {/* -------------------------------------------------------------------------------------------------
       * Asset Balance
       * -----------------------------------------------------------------------------------------------*/}
      <Tooltip
        trigger={
          <div className="grid w-fit gap-2 text-right">
            <Text size="2xs" tag="span" variant="silent">
              {t("balance", { ns: "glossary" })}
            </Text>
            <Balance
              locale={currentLocale}
              privacy={privacyEnabled}
              size="sm"
              showSymbol={SundaeUtils.isAdaAsset(asset.metadata)}
              value={totalAssetAmount}
            />
          </div>
        }
        tooltipLabel={
          <Balance
            abbreviate={false}
            locale={currentLocale}
            showSymbol={false}
            size="sm"
            value={totalAssetAmount}
          />
        }
      />
    </li>
  );
};

export default NewPositionItem;
